import { Card, CardContent } from '@mlm/ui';
import type { GiveMeProgressRow } from '../../lib/api';
import { fmtCurrency, fmtNumber } from '../../lib/format';

function pct(have: number, need: number): number {
  if (need <= 0) return 100;
  return Math.max(0, Math.min(100, (have / need) * 100));
}

/**
 * Give Me Bonus milestone counter — one bar per applicable (setting ×
 * direct-tier) cell for the member's own tier: "1/4 · to receive ₱5,000.00".
 * `progress` is `count % N`, so right after a batch pays it honestly reads
 * "0/4" with an "earned k×" tally for the batches already collected.
 */
export function GiveMePanel({ rows, title }: { rows: GiveMeProgressRow[]; title?: string }) {
  if (rows.length === 0) return null;
  return (
    <Card>
      <CardContent className="p-6">
        <p className="text-caption font-medium uppercase tracking-[0.1em] text-muted-foreground">
          {title ?? 'Give Me Bonus'}
        </p>
        <div className="mt-3 space-y-3">
          {rows.map((row) => {
            const p = pct(row.progress, row.directsRequired);
            const label = `${row.settingLabel ?? 'Milestone'} · ${
              row.unified ? 'All packages' : `${row.directTierName ?? row.directTierCode} directs`
            }`;
            return (
              <div key={`${row.settingIndex}:${row.directTierCode ?? '*'}:${row.directsRequired}`} className="min-w-0">
                <div className="flex items-baseline justify-between gap-2">
                  <span className="truncate text-caption text-muted-foreground">{label}</span>
                  <span className="text-caption tabular-nums text-foreground">
                    {fmtNumber(row.progress)} / {fmtNumber(row.directsRequired)}
                  </span>
                </div>
                <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-muted">
                  <div className="h-full rounded-full bg-primary" style={{ width: `${p}%` }} />
                </div>
                <p className="mt-1 text-caption text-muted-foreground">
                  to receive {fmtCurrency(row.reward)}
                  {row.batchesEarned > 0 ? (
                    <span className="text-emerald-600"> · earned {row.batchesEarned}×</span>
                  ) : null}
                </p>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
